import React, { useState, useEffect } from 'react';
import { FaTrophy, FaStar, FaMedal, FaFilter, FaChartLine, FaUsers, FaBullhorn, FaTimes, FaUserGraduate, FaAward, FaFilePdf, FaUserTie } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as adminService from '../services/adminService';
import * as masterService from '../services/masterService';


const SelectedStudents = () => {
  const [toast, setToast] = useState(null);
  const [runningTitles, setRunningTitles] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [rankedStudents, setRankedStudents] = useState([]);
  const [resultsDeclared, setResultsDeclared] = useState(false);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [topStudents, setTopStudents] = useState([]);
  const [reviewersData, setReviewersData] = useState({});
  const [streamsMap, setStreamsMap] = useState({});
  const [specializationsMap, setSpecializationsMap] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || currentUser.role !== 'admin') {
      navigate('/login');
      return;
    }

    loadInitialData();
  }, [navigate]);

  useEffect(() => {
    if (selectedTitle && allArticles.length > 0) {
      calculateRankings(selectedTitle.id, allArticles, resultsDeclared);
    }
  }, [allArticles, selectedTitle, resultsDeclared, streamsMap, specializationsMap]);

  const loadInitialData = async () => {
    await Promise.all([
      loadRunningTitles(),
      loadArticles(),
      loadMasterData()
    ]);
  };

  const loadMasterData = async () => {
    try {
      const [streamsData, specializationsData] = await Promise.all([
        masterService.getAllStreams(),
        masterService.getAllSpecializations()
      ]);

      const unwrap = (res) => Array.isArray(res) ? res : (res.data || []);

      const sMap = unwrap(streamsData).reduce((acc, m) => ({ ...acc, [m.id]: m.name }), {});
      const specMap = unwrap(specializationsData).reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {});

      setStreamsMap(sMap);
      setSpecializationsMap(specMap);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const loadArticles = async () => {
    try {
      const response = await adminService.getSubmittedArticles();
      const articles = response.data || response || [];

      const normalized = articles.map(article => {
        // Map submission data to expected format
        const student = article.submitter || {};
        const runTitle = article.running_title || {};

        return {
          ...article,
          id: article.id,
          runningTitleId: article.running_title_id,
          // Map user/student details
          studentName: student.full_name || 'Unknown',
          university: article.university || '-',
          userName: student.full_name, // For backward compatibility if needed
          user: student,
          // Map title details if needed context
          articleTitle: typeof article.title === 'string' ? article.title : (runTitle.title || 'Untitled'),
          subject: article.subject_id || '-', // Subject name might need to be derived from context or another call if IDs are returned
          // Ensure reviews array exists
          reviews: article.reviews || [],
          status: article.status,
          resultDate: article.result_date || article.resultDate || runTitle.result_date || runTitle.resultDate || runTitle.declared_at || null
        };
      });
      setAllArticles(normalized);
    } catch (error) {
      console.error('Error loading articles:', error);
    }
  };

  const loadRunningTitles = async () => {
    try {
      const response = await adminService.getAllRunningTitles();
      let titles = [];

      if (response && response.success && Array.isArray(response.data)) {
        titles = response.data;
      } else if (Array.isArray(response)) {
        titles = response;
      }

      // Map API fields to what the component expects
      const mappedTitles = titles.map(title => {
        // Resolve Subject Name
        let subjectName = title.subject_name;
        if (!subjectName && title.subject) {
          if (typeof title.subject === 'object' && title.subject !== null) {
            subjectName = title.subject.name;
          } else {
            subjectName = title.subject;
          }
        }

        return {
          ...title,
          id: title.id || title._id,
          articleTitle: title.title || title.articleTitle,
          subject: subjectName || '-',
          submissionHeadline: title.submission_deadline || title.submissionDeadline || title.running_title?.submission_deadline || title.running_title?.submissionDeadline,
          submissionDeadline: title.submission_deadline || title.submissionDeadline || title.running_title?.submission_deadline || title.running_title?.submissionDeadline,
          resultDate: title.result_date || title.resultDate || title.running_title?.result_date || title.running_title?.resultDate || title.declared_at || title.published_at || title.running_title?.declared_at
        };
      });

      // Filter titles that have a deadline (this acts as "configured")
      const configured = mappedTitles.filter(title => title.submissionDeadline);
      setRunningTitles(configured);
    } catch (error) {
      console.error('Error loading running titles:', error);
      showToast('Failed to load running titles', 'error');
    }
  };

  const handleTitleSelect = (e) => {
    const titleId = e.target.value;
    if (!titleId) {
      setSelectedTitle(null);
      setRankedStudents([]);
      setResultsDeclared(false);
      return;
    }

    const title = runningTitles.find(t => String(t.id) === String(titleId));
    setSelectedTitle(title);

    // Check if results are already declared for this title by checking if any article has 'winner' status
    const isDeclared = allArticles.some(article => {
      const articleTitleId = article.running_title_id || article.runningTitleId;
      return String(articleTitleId) === String(titleId) && article.status?.toLowerCase() === 'winner';
    });
    setResultsDeclared(isDeclared);

    calculateRankings(titleId, allArticles, isDeclared);
  };

  const calculateRankings = (titleId, articles = allArticles, isDeclared = false) => {
    // Filter articles for this running title
    const filteredArticles = articles.filter(article => {
      const articleTitleId = article.running_title_id || article.runningTitleId;
      const status = (article.status || '').toLowerCase();

      return String(articleTitleId) === String(titleId) &&
        (status === 'in-review' ||
          status === 'in_review' ||
          status === 'reviewed' ||
          status === 'winner' ||
          status === 'checked' ||
          status === 'submitted' ||
          status === 'approved' ||
          status === 'accepted' ||
          status === 'assigned' ||
          status === 'in_queue' ||
          status === 'pending');
    });

    // Calculate marks for each student
    const studentsData = filteredArticles.map(article => {
      let rating = 0;
      const reviewers = [];

      // Resolve subject and university names if they are objects
      const contextTitle = runningTitles.find(t => String(t.id) === String(titleId));
      const subjectName = contextTitle ? contextTitle.subject : (typeof article.subject === 'object' && article.subject !== null ? article.subject.name : article.subject);

      const universityName = typeof article.university === 'object' && article.university !== null ? article.university.name : article.university;
      const studentName = article.userName || (article.user ? (article.user.full_name || article.user.name) : 'Unknown');

      // Robust rating extraction (matching AllArticlesYoungIndia)
      const reviewFromList = article.reviews && article.reviews.length > 0 ? article.reviews[0] : null;
      const extractedRating = article.rating ??
        article.reviewRating ??
        article.score ??
        article.avgRating ??
        article.review?.rating ??
        reviewFromList?.rating;

      if (extractedRating !== undefined && extractedRating !== null) {
        rating = Number(extractedRating);
      }

      // Collect reviewers
      if (article.reviews && Array.isArray(article.reviews)) {
        article.reviews.forEach(rev => {
          if (rev.reviewerName) reviewers.push(rev.reviewerName);
          else if (rev.reviewedBy) reviewers.push(rev.reviewedBy.full_name || rev.reviewedBy.name);
          else if (rev.reviewer) reviewers.push(rev.reviewer.full_name || rev.reviewer.name);
        });
      } else if (article.review && article.review.reviewerName) {
        reviewers.push(article.review.reviewerName);
      }

      // Extract specific reviewer and supervisor for the table
      const firstReview = article.reviews && article.reviews.length > 0 ? article.reviews[0] : null;
      const assignedTo = article.assigned_to || article.assignedTo || {};

      let linkedReviewer = '-';
      let supervisorName = '-';

      if (firstReview) {
        // If reviewedBy exists, it's the actual person who did the review (likely Sub-EB)
        if (firstReview.reviewedBy) {
          linkedReviewer = firstReview.reviewedBy.full_name || firstReview.reviewedBy.name || '-';
          // In this case, the 'reviewer' field usually contains the supervisor/assigner
          supervisorName = firstReview.reviewer?.full_name || '-';
        } else {
          // If no reviewedBy, use reviewer as the primary reviewer
          linkedReviewer = firstReview.reviewerName ||
            firstReview.reviewer?.full_name ||
            (firstReview.reviewedBy ? (firstReview.reviewedBy.full_name || firstReview.reviewedBy.name) : '-');

          // Use the supervisor linked to the reviewer if available
          supervisorName = firstReview.reviewer?.supervisor?.full_name ||
            firstReview.supervisor?.full_name ||
            firstReview.supervisorName || '-';
        }
      } else if (assignedTo && (assignedTo.professorName || assignedTo.name)) {
        // Fallback to assignment info if no review exists yet
        linkedReviewer = assignedTo.professorName || assignedTo.name || (assignedTo.full_name) || '-';
        // Priority to supervisor info if present in assignment
        supervisorName = assignedTo.supervisor?.full_name || assignedTo.supervisor?.name || assignedTo.supervisorName || '-';
      }

      // Final fallback: check if article itself has these fields
      if ((linkedReviewer === '-' || linkedReviewer === 'N/A') && (article.reviewer_name || article.reviewerName)) {
        linkedReviewer = article.reviewer_name || article.reviewerName;
      }
      if ((supervisorName === '-' || supervisorName === 'N/A') && (article.supervisor_name || article.supervisorName)) {
        supervisorName = article.supervisor_name || article.supervisorName;
      }

      // Check if article has result declaration data
      const declaredRank = article.declaredRank || null;
      const declaredAverage = article.declaredAverage || rating;

      return {
        studentName: studentName,
        university: universityName || '-',
        subject: subjectName || '-',
        stream: streamsMap[article.stream_id] || article.stream?.name || article.stream || '-',
        specialization: specializationsMap[article.specialization_id] || article.specialization?.name || article.specialization || '-',
        fileUrl: article.file_url,
        reviewer: linkedReviewer,
        supervisor: supervisorName,
        averageMarks: isDeclared ? declaredAverage : rating,
        finalAverage: declaredAverage,
        declaredRank: declaredRank,
        status: article.status,
        articleId: article.id,
        userId: article.userId,
        resultDate: article.resultDate,
        reviewers: [...new Set(reviewers)], // Remove duplicates
        articleTitle: article.articleTitle
      };
    });

    // Sort by average marks (highest first)
    let ranked = studentsData;
    if (isDeclared) {
      // When results are declared, use declared rank if available, else calculate
      ranked = studentsData.sort((a, b) => {
        if (a.declaredRank && b.declaredRank) {
          return a.declaredRank - b.declaredRank;
        }
        return b.averageMarks - a.averageMarks;
      });
    } else {
      ranked = studentsData.sort((a, b) => b.averageMarks - a.averageMarks);
    }

    // Add rank position
    const rankedWithPosition = ranked.map((student, index) => ({
      ...student,
      rank: student.declaredRank || index + 1
    }));

    setRankedStudents(rankedWithPosition);
  };

  const prepareDeclarationData = () => {
    if (!selectedTitle || rankedStudents.length === 0) return;

    // Check if result date has passed
    if (selectedTitle.resultDate) {
      const now = new Date();
      const resultDate = new Date(selectedTitle.resultDate);

      // Compare dates only (ignoring time)
      const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const resultDateOnly = new Date(resultDate.getFullYear(), resultDate.getMonth(), resultDate.getDate());

      if (nowDateOnly < resultDateOnly) {
        showToast(`Results can only be declared starting from ${resultDate.toLocaleDateString()}`, 'error');
        return;
      }
    }

    // Get top 5 students
    const topFive = rankedStudents.slice(0, 5).map(student => ({
      rank: student.rank,
      name: student.studentName,
      average: student.averageMarks.toFixed(2),
      articleTitle: student.articleTitle,
      university: student.university,
      stream: student.stream,
      specialization: student.specialization,
      reviewer: student.reviewer,
      supervisor: student.supervisor,
      reviewers: student.reviewers
    }));

    setTopStudents(topFive);
    setShowDeclarationModal(true);
  };

  const confirmDeclaration = async () => {
    if (!selectedTitle) {
      showToast('Please select a title first', 'error');
      return;
    }

    if (rankedStudents.length === 0) {
      showToast('No students found to declare results', 'error');
      return;
    }

    try {
      // Use the API to publish results
      // Assuming rankedStudents is sorted by rank/score, we use the winner's article ID or logic as per backend requirement
      const articleIdToPublish = rankedStudents[0]?.articleId;

      if (!articleIdToPublish) {
        showToast('No article found to publish', 'error');
        return;
      }

      const response = await adminService.publishArticle(articleIdToPublish);

      if (response && response.success) {
        // Clear the local storage items used for mocking/local state


        setResultsDeclared(true);
        showToast(response.message || 'Results declared successfully! Top 5 students published.', 'success');

        // Update the winners if returned
        if (response.winners) {
          const mappedWinners = response.winners.map(w => ({
            rank: w.rank || 0,
            name: w.student_name || 'Unknown',
            average: (w.total_marks !== undefined && w.total_marks !== null) ? w.total_marks.toString() : '0',
            articleTitle: w.title || 'Untitled',
            articleId: w.article_id
          }));
          setTopStudents(mappedWinners);
        }

        // Close modal and refresh rankings if possible
        setShowDeclarationModal(false);
        await loadArticles(); // Refresh data from server
      } else {
        showToast(response.message || 'Failed to declare results', 'error');
        await loadArticles(); // Check state anyway
      }
    } catch (error) {
      console.error('Error declaring results:', error);

      let errorMessage = 'Failed to declare results.';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.error) {
        errorMessage = error.error;
      }

      // Check for common backend states that might come as errors (e.g. 400 Bad Request if already done)
      if (errorMessage.toLowerCase().includes('already') ||
        errorMessage.toLowerCase().includes('published') ||
        errorMessage.toLowerCase().includes('declared')) {
        showToast('Results appear to be already declared. Refreshing...', 'info');
        setResultsDeclared(true);
        setShowDeclarationModal(false);
      } else {
        showToast(errorMessage + ' Please try again.', 'error');
      }

      await loadArticles(); // Verify backend state to fix UI if it was actually successful
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const getRankBadge = (rank) => {
    if (rank === 1) {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-full font-bold shadow-lg">
          <FaTrophy className="text-xl" />
          <span>1st</span>
        </div>
      );
    } else if (rank === 2) {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-300 to-gray-400 text-white rounded-full font-bold shadow-lg">
          <FaMedal className="text-xl" />
          <span>2nd</span>
        </div>
      );
    } else if (rank === 3) {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-full font-bold shadow-lg">
          <FaMedal className="text-xl" />
          <span>3rd</span>
        </div>
      );
    } else {
      return (
        <div className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-bold">
          {rank}th
        </div>
      );
    }
  };

  const getAverageColor = (average) => {
    if (average >= 9) return 'text-green-600';
    if (average >= 8) return 'text-blue-600';
    if (average >= 7) return 'text-yellow-600';
    if (average >= 6) return 'text-orange-600';
    return 'text-red-600';
  };

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-primary-blue transition-all text-sm bg-white";

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Declaration Modal */}
      {showDeclarationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 animate-fade-in">
            {/* Modal Header */}
            <div className="p-6 rounded-t-2xl" style={darkGradient}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">Declare Results</h2>
                  <p className="text-blue-100">Review the top 5 students before declaring final results</p>
                </div>
                <button
                  onClick={() => setShowDeclarationModal(false)}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Selected Title Info */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-bold text-gray-900 mb-2">Title: {selectedTitle?.articleTitle}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Department</p>
                    <p className="font-semibold">{selectedTitle?.subject}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Students</p>
                    <p className="font-semibold">{rankedStudents.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-semibold">{new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Top 5 Students Table */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaTrophy className="text-yellow-500" />
                  Top 5 Students
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rank</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Student Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Stream/Specialization</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">University</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Article Impact</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Editorial Team</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {topStudents.map((student, index) => (
                        <tr
                          key={index}
                          className={`
                            ${student.rank === 1 ? 'bg-yellow-50' : ''}
                            ${student.rank === 2 ? 'bg-gray-50' : ''}
                            ${student.rank === 3 ? 'bg-orange-50' : ''}
                            hover:bg-blue-50
                          `}
                        >
                          <td className="px-4 py-3">
                            {getRankBadge(student.rank)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">{student.name}</div>
                            <div className="text-sm text-gray-500">{student.articleTitle}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            <div className="text-sm font-medium">{student.stream}</div>
                            <div className="text-xs text-gray-500">{student.specialization}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700 text-sm">{student.university}</td>
                          <td className="px-4 py-3">
                            <div className={`text-xl font-bold ${getAverageColor(student.average)}`}>
                              {student.average}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <div className="text-xs text-gray-600 flex items-center gap-1">
                                <FaUserTie className="text-purple-500" />
                                <span className="font-medium">EB:</span> {student.supervisor}
                              </div>
                              <div className="text-xs text-gray-600 flex items-center gap-1">
                                <FaUserGraduate className="text-primary-blue" />
                                <span className="font-medium">Assoc:</span> {student.reviewer}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Note */}
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <FaAward className="text-yellow-500 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Note:</p>
                    <p className="text-sm text-gray-700">
                      These top 5 students will be displayed on the home page under "Recent Results".
                      Once declared, the results cannot be changed and will be visible to all students.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-end">
                <button
                  onClick={() => setShowDeclarationModal(false)}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeclaration}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                >
                  <FaBullhorn className="text-lg" />
                  Confirm & Declare Results
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
        <div className="max-w mx-auto px-4">
          {/* Header */}
          <div className="rounded-2xl shadow-lg p-6 mb-6" style={darkGradient}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Show Selected Students</h1>
                <p className="text-blue-100">View all students in competition with live rankings</p>
              </div>
              <div className="hidden md:block">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <FaTrophy className="text-3xl text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Title Selection */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <FaFilter className="text-primary-blue text-xl" />
              <h2 className="text-xl font-bold text-gray-900">Select Running Title</h2>
            </div>

            <div className="max-w-2xl">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Running Article Title <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedTitle?.id || ''}
                onChange={handleTitleSelect}
                className={inputClass}
              >
                <option value="">-- Select a Running Title --</option>
                {runningTitles.map((title) => {
                  const isTitleDeclared = allArticles.some(article => {
                    const articleTitleId = article.running_title_id || article.runningTitleId;
                    return String(articleTitleId) === String(title.id) && article.status?.toLowerCase() === 'winner';
                  });

                  return (
                    <option key={title.id} value={title.id}>
                      {title.articleTitle} ({title.subject})
                      {isTitleDeclared && ' ✓'}
                    </option>
                  );
                })}
              </select>

              {runningTitles.length === 0 && (
                <p className="text-sm text-orange-600 mt-2">
                  No configured running titles found.
                </p>
              )}
            </div>
          </div>

          {/* Selected Title Info with Declare Button */}
          {selectedTitle && (
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border-2 border-blue-200 p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Selected Title</p>
                  <p className="font-bold text-gray-900">{selectedTitle.articleTitle}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Department</p>
                  <p className="font-bold text-gray-900">{selectedTitle.subject}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Result Date</p>
                  <p className={`font-bold ${selectedTitle.resultDate ? 'text-gray-900' : 'text-orange-600 italic'}`}>
                    {selectedTitle.resultDate ? new Date(selectedTitle.resultDate).toLocaleDateString() : 'Not Configured'}
                  </p>
                </div>
                <div className="flex items-end">
                  {!resultsDeclared ? (
                    <div className="w-full">
                      <button
                        onClick={prepareDeclarationData}
                        disabled={selectedTitle.resultDate && new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()) < new Date(new Date(selectedTitle.resultDate).getFullYear(), new Date(selectedTitle.resultDate).getMonth(), new Date(selectedTitle.resultDate).getDate())}
                        className={`w-full py-3 font-bold rounded-lg shadow-lg transition-all flex items-center justify-center gap-2 ${selectedTitle.resultDate && new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()) < new Date(new Date(selectedTitle.resultDate).getFullYear(), new Date(selectedTitle.resultDate).getMonth(), new Date(selectedTitle.resultDate).getDate())
                          ? 'bg-gray-400 cursor-not-allowed opacity-75 text-gray-200'
                          : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white hover:shadow-xl'
                          }`}
                      >
                        <FaBullhorn className="text-lg" />
                        Declare Results
                      </button>
                      {selectedTitle.resultDate && new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()) < new Date(new Date(selectedTitle.resultDate).getFullYear(), new Date(selectedTitle.resultDate).getMonth(), new Date(selectedTitle.resultDate).getDate()) && (
                        <p className="text-[10px] text-red-600 mt-1 font-medium text-center italic">
                          Available on {new Date(selectedTitle.resultDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-bold rounded-lg shadow-lg text-center">
                      Results Declared ✓
                    </div>
                  )}
                </div>
              </div>

              {resultsDeclared && (
                <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-100 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-700">
                    <FaBullhorn />
                    <span className="font-semibold">Results have been declared for this title.</span>
                    <span className="text-sm">Top 5 students are now visible on the home page.</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rankings Table */}
          {selectedTitle && rankedStudents.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="p-6" style={darkGradient}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-1">
                      Student Rankings {resultsDeclared ? '- Final Results' : '- Live'}
                    </h2>
                    <p className="text-blue-100">
                      {resultsDeclared
                        ? 'Final rankings declared and visible to all students'
                        : 'Ranked by current score • Updates in real-time'}
                    </p>
                  </div>
                  <div className="bg-white/20 px-4 py-2 rounded-lg">
                    <p className="text-white text-sm">Total Students</p>
                    <p className="text-white text-2xl font-bold">{rankedStudents.length}</p>
                  </div>
                </div>
              </div>

              <div className="overflow-hidden">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="w-24 px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Rank</th>
                      <th className="w-48 px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Student Name</th>
                      <th className="w-48 px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">University</th>
                      <th className="w-32 px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Department</th>
                      <th className="w-32 px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Stream</th>
                      <th className="w-32 px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Specialization</th>
                      <th className="w-24 px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Article</th>
                      <th className="w-32 px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">EB Member</th>
                      <th className="w-32 px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Associate</th>
                      <th className="w-28 px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">{resultsDeclared ? 'Final Impact' : 'Article Impact'}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rankedStudents.map((student) => (
                      <tr
                        key={student.articleId}
                        className={`hover:bg-blue-50 transition-colors ${student.rank === 1 ? 'bg-yellow-50' :
                          student.rank === 2 ? 'bg-gray-50' :
                            student.rank === 3 ? 'bg-orange-50' : ''
                          }`}
                      >
                        {/* Rank */}
                        <td className="px-4 py-4">
                          {getRankBadge(student.rank)}
                          {resultsDeclared && student.declaredRank && (
                            <span className="text-xs text-green-600 ml-2">✓ Final</span>
                          )}
                        </td>

                        {/* Student Name */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {student.rank <= 3 && (
                              <FaStar className={
                                student.rank === 1 ? 'text-yellow-500' :
                                  student.rank === 2 ? 'text-gray-400' :
                                    'text-orange-500'
                              } />
                            )}
                            <span className="font-semibold text-gray-900 break-words">{student.studentName}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1 line-clamp-2" title={student.articleTitle}>{student.articleTitle}</div>
                        </td>

                        {/* University */}
                        <td className="px-4 py-4">
                          <span className="text-gray-700 text-sm break-words line-clamp-3" title={student.university}>{student.university}</span>
                        </td>

                        {/* Subject */}
                        <td className="px-4 py-4">
                          <span className="text-gray-700 text-sm break-words">{student.subject}</span>
                        </td>

                        {/* Stream */}
                        <td className="px-4 py-4">
                          <span className="text-gray-600 text-sm font-medium break-words">{student.stream}</span>
                        </td>

                        {/* Specialization */}
                        <td className="px-4 py-4">
                          <span className="text-gray-600 text-sm font-medium break-words">{student.specialization}</span>
                        </td>

                        {/* PDF Article */}
                        <td className="px-4 py-4 text-center">
                          {student.fileUrl ? (
                            <a
                              href={student.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-2 py-1.5 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-colors border border-red-100"
                              title="View PDF"
                            >
                              <FaFilePdf size={14} />
                              <span className="text-xs">PDF</span>
                            </a>
                          ) : (
                            <span className="text-gray-400 text-xs text-nowrap">No File</span>
                          )}
                        </td>

                        {/* Supervisor */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <FaUserTie className="text-purple-500 text-sm flex-shrink-0" />
                            <span className="text-gray-700 text-sm font-medium break-words">{student.supervisor}</span>
                          </div>
                        </td>

                        {/* Reviewer */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <FaUserGraduate className="text-blue-500 text-sm flex-shrink-0" />
                            <span className="text-gray-700 text-sm font-medium break-words">{student.reviewer}</span>
                          </div>
                        </td>

                        {/* Current/Final Score */}
                        <td className="px-4 py-4 text-center">
                          <div className="flex flex-col items-center">
                            <div className={`text-xl font-bold ${getAverageColor(student.averageMarks)}`}>
                              {student.averageMarks.toFixed(2)}
                            </div>
                            <div className="text-[10px] text-gray-500 uppercase">
                              {resultsDeclared ? 'Final' : 'Current'}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary Statistics */}
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 border-t-2 border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                        <FaTrophy className="text-yellow-600 text-xl" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Top Score</p>
                        <p className="text-xl font-bold text-gray-900">
                          {rankedStudents[0]?.averageMarks.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                        <FaChartLine className="text-red-600 text-xl" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Lowest Score</p>
                        <p className="text-xl font-bold text-gray-900">
                          {rankedStudents[rankedStudents.length - 1]?.averageMarks.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <FaStar className="text-green-600 text-xl" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Overall Score</p>
                        <p className="text-xl font-bold text-gray-900">
                          {(rankedStudents.reduce((sum, s) => sum + s.averageMarks, 0) / rankedStudents.length).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FaMedal className="text-purple-600 text-xl" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Reviewed</p>
                        <p className="text-xl font-bold text-gray-900">
                          {rankedStudents.filter(s => s.status === 'reviewed' || s.status === 'checked').length}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FaUsers className="text-blue-600 text-xl" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">In Competition</p>
                        <p className="text-xl font-bold text-gray-900">
                          {rankedStudents.length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No Students Found */}
          {selectedTitle && rankedStudents.length === 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <FaTrophy className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Students in Competition Yet</h3>
              <p className="text-gray-500">
                No students have started the review process for this title yet.
              </p>
            </div>
          )}

          {/* No Title Selected */}
          {!selectedTitle && (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <FaFilter className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">Select a Running Title</h3>
              <p className="text-gray-500">
                Please select a running title from the dropdown above to view student rankings
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SelectedStudents;